import { useMutation, useQueryClient } from '@tanstack/react-query';
import { invoiceDetailQueryKey } from '@/hooks/useInvoices';
import { useClinic } from '@/hooks/useClinicSettings';
import {
  deriveClinicPayDisplayStatus,
  useClinicPayStatusLocal,
  useRequestClinioPayLink,
  type RequestPayLinkOutput,
} from '@/hooks/useClinicPay';
import { isClinioPayAvailableForStaff } from '@/lib/calendarPaymentChoice';
import { parseBookingPaymentMethods, paymentMethodIsEnabled } from '@/lib/bookingPolicies';
import { supabase } from '@/lib/supabase';
import { paymentRequestsQueryKey } from '@/hooks/useInvoicePaymentRequests';

export function useMobilePayRequestGate(clinicId: string | null | undefined) {
  const { data: clinic, isFetched: clinicFetched } = useClinic(clinicId);
  const { data: payStatus, isFetched: payFetched } = useClinicPayStatusLocal(clinicId);
  const methods = parseBookingPaymentMethods(clinic?.booking_payment_methods);
  return {
    ready: Boolean(clinicId) && clinicFetched && payFetched,
    mobilePayEnabled: paymentMethodIsEnabled(methods, 'mobilepay'),
    clinioPayAvailable: isClinioPayAvailableForStaff({
      useClinioPay: clinic?.use_clinio_pay,
      payDisplayStatus: deriveClinicPayDisplayStatus(payStatus),
    }),
  };
}

/**
 * Invoice MobilePay request. Reuses pay-create-checkout via useRequestClinioPayLink.
 * Creates a payment request. Does not insert invoice_payments and does not mark
 * the appointment paid.
 */
export function useInvoiceMobilePayRequest() {
  const queryClient = useQueryClient();
  const requestPayLink = useRequestClinioPayLink();

  return useMutation({
    mutationFn: async (invoiceId: string): Promise<RequestPayLinkOutput> => {
      return requestPayLink.mutateAsync({
        invoiceId,
        paymentMethod: 'mobilepay',
      });
    },
    onSuccess: (_pay, invoiceId) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoices', invoiceId] });
      queryClient.invalidateQueries({ queryKey: invoiceDetailQueryKey(invoiceId) });
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      queryClient.invalidateQueries({ queryKey: ['calendar', 'invoices'] });
      queryClient.invalidateQueries({ queryKey: paymentRequestsQueryKey(invoiceId) });
    },
  });
}

/** Drops one unpaid payment request. Other requests on the invoice stay open. */
export function useCancelPaymentRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { requestId: string; invoiceId: string }) => {
      const { data, error } = await supabase.rpc(
        'cancel_payment_request' as never,
        { p_request_id: input.requestId } as never,
      );
      if (error) throw error;
      return data;
    },
    onSuccess: (_data, input) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoices', input.invoiceId] });
      queryClient.invalidateQueries({ queryKey: invoiceDetailQueryKey(input.invoiceId) });
      queryClient.invalidateQueries({ queryKey: paymentRequestsQueryKey(input.invoiceId) });
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
    },
  });
}

/** Drops the unpaid Clinio Pay request. The invoice stays open for another method. */
export function useCancelProviderPaymentRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (invoiceId: string) => {
      const { data, error } = await supabase.rpc(
        'cancel_provider_payment_request' as never,
        { p_invoice_id: invoiceId } as never,
      );
      if (error) throw error;
      return data;
    },
    onSuccess: (_data, invoiceId) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoices', invoiceId] });
      queryClient.invalidateQueries({ queryKey: invoiceDetailQueryKey(invoiceId) });
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      queryClient.invalidateQueries({ queryKey: ['appointments', 'invoice-link'] });
      queryClient.invalidateQueries({ queryKey: ['calendar', 'invoices'] });
      queryClient.invalidateQueries({ queryKey: paymentRequestsQueryKey(invoiceId) });
    },
  });
}
